import { z } from 'zod';
import { Cents, IsoDate, IsoDateTime, Uuid } from './common.js';
import { Expense, ExpenseExclusionReason } from './data.js';
import { MonthKey, YearEndProjection } from './metrics.js';

/**
 * Lot P « Dépenses réelles, catégories prédites par l'IA, estimation de fin d'année » (décisions de Maxime du
 * 13/10/2026). Modifications internes, sans plan (CDC §3.4) ; l'IA est une lecture (aucune écriture externe).
 */

// ------------------------------------------------------------------ Règles apprises

export const ExpenseRuleField = z.enum(['label', 'counterparty']);
export type ExpenseRuleField = z.infer<typeof ExpenseRuleField>;

/** Règle « libellé (ou fournisseur) contenant … → poste », prioritaire sur le compte comptable et l'IA. */
export const ExpenseRule = z.object({
  id: Uuid,
  field: ExpenseRuleField,
  /** Mots du libellé en majuscules sans accents ; les nombres (dates, références) sont ignorés. */
  pattern: z.string(),
  categoryId: Uuid,
  categoryName: z.string(),
  createdAt: IsoDateTime,
});
export type ExpenseRule = z.infer<typeof ExpenseRule>;

/** `POST /api/expense-rules` : crée (ou remplace) la règle et l'applique aux lignes existantes. */
export const CreateExpenseRuleBody = z.object({
  field: ExpenseRuleField,
  pattern: z.string().trim().min(1).max(200),
  categoryId: Uuid,
});
export type CreateExpenseRuleBody = z.infer<typeof CreateExpenseRuleBody>;

export const CreateExpenseRuleResult = z.object({
  rule: ExpenseRule,
  /** Lignes existantes rangées par la règle (hors postes choisis à la main). */
  applied: z.int().min(0),
});
export type CreateExpenseRuleResult = z.infer<typeof CreateExpenseRuleResult>;

// ------------------------------------------------------------------ Catégorisation par l'IA

/**
 * `POST /api/expenses/categorize-ai` (et après chaque synchro Axonaut) : Claude propose un poste aux lignes sans
 * poste. `ok` : Claude a répondu ; `demo` : prédictions de démonstration (mots-clés) ; `not_configured` : Claude
 * n'est pas configuré (règles et comptes comptables s'appliquent toujours) ; `cap_reached` : plafond mensuel
 * atteint ; `nothing` : aucune ligne à catégoriser ; `error` : Claude n'a pas répondu (rien n'est perdu).
 */
export const AiCategorizeResult = z.object({
  status: z.enum(['ok', 'demo', 'not_configured', 'cap_reached', 'nothing', 'error']),
  /** Lignes soumises. */
  attempted: z.int().min(0),
  /** Lignes qui ont reçu un poste. */
  categorized: z.int().min(0),
  /** Parmi elles, prédictions peu sûres (« À vérifier »). */
  review: z.int().min(0),
  /** Phrase lisible pour l'écran. */
  note: z.string(),
});
export type AiCategorizeResult = z.infer<typeof AiCategorizeResult>;

// ------------------------------------------------------------------ Postes

export const ExpenseCategoryKind = z.enum(['fixed', 'variable']);
export type ExpenseCategoryKind = z.infer<typeof ExpenseCategoryKind>;
/** Nom d'un poste : 1 à 80 caractères, espaces de bord retirés. */
export const ExpenseCategoryName = z.string().trim().min(1, 'Donne un nom au poste.').max(80);

/**
 * `PATCH /api/expense-categories/:id` (admin) : TVA récupérable (lot P), nom et archivage (lot AH [21]). Un
 * poste archivé garde son passé mais disparaît des listes, de l'IA et des mois à venir ; `archived: false` le
 * rétablit.
 */
export const UpdateExpenseCategoryBody = z
  .object({
    vatRecoverable: z.boolean().optional(),
    name: ExpenseCategoryName.optional(),
    archived: z.boolean().optional(),
  })
  .refine((b) => b.vatRecoverable !== undefined || b.name !== undefined || b.archived !== undefined, {
    message: 'Aucune modification demandée',
  });
export type UpdateExpenseCategoryBody = z.infer<typeof UpdateExpenseCategoryBody>;

export const ExpenseCategoryVat = z.object({ id: Uuid, name: z.string(), vatRecoverable: z.boolean() });
export type ExpenseCategoryVat = z.infer<typeof ExpenseCategoryVat>;

/** Poste renvoyé par les écritures du lot AH (création, modification, éclatement). */
export const ExpenseCategorySummary = ExpenseCategoryVat.extend({
  key: z.string(),
  kind: ExpenseCategoryKind,
  archived: z.boolean(),
});
export type ExpenseCategorySummary = z.infer<typeof ExpenseCategorySummary>;

/** `POST /api/expense-categories` (admin, lot AH [21]) : nouveau poste, aussitôt dans le tableau et les listes. */
export const CreateExpenseCategoryBody = z.object({
  name: ExpenseCategoryName,
  kind: ExpenseCategoryKind,
  vatRecoverable: z.boolean(),
});
export type CreateExpenseCategoryBody = z.infer<typeof CreateExpenseCategoryBody>;

/**
 * `POST /api/expense-categories/:id/split` (admin, lot AH [21]) : nouveau poste (même type de charge) qui reçoit
 * les estimations de l'ancien à partir de `fromMonth` (mois en cours ou à venir de l'année) ; le passé, les
 * règles et les comptes Axonaut restent sur l'ancien poste.
 */
export const SplitExpenseCategoryBody = z.object({
  name: ExpenseCategoryName,
  vatRecoverable: z.boolean().optional(),
  fromMonth: MonthKey,
});
export type SplitExpenseCategoryBody = z.infer<typeof SplitExpenseCategoryBody>;

export const SplitExpenseCategoryResult = z.object({
  category: ExpenseCategorySummary,
  /** Mois dont l'estimation a été transférée (de `fromMonth` à décembre). */
  months: z.array(MonthKey),
});
export type SplitExpenseCategoryResult = z.infer<typeof SplitExpenseCategoryResult>;

/** Un poste dans Réglages > Dépenses (`GET /api/expense-categories`, admin). */
export const ExpenseCategoryItem = ExpenseCategorySummary.extend({
  position: z.int(),
  archivedAt: IsoDateTime.nullable(),
  archivedByName: z.string().nullable(),
  /** Créé dans le Cockpit (null : importé du Sheet ou poste de démonstration). */
  createdByName: z.string().nullable(),
  /** Né de l'éclatement d'un autre poste. */
  splitFrom: z.object({ id: Uuid, name: z.string(), month: MonthKey }).nullable(),
  /** Moyenne des estimations des mois restants de l'année (0 pour un poste archivé). */
  monthlyEstimateCents: Cents,
  /** Dépenses rangées dans ce poste (toutes années, lignes du Sheet comprises). */
  linesCount: z.int().min(0),
  /** Règles apprises et comptes Axonaut qui rangent des lignes dans ce poste. */
  rulesCount: z.int().min(0),
  accountCodes: z.array(z.string()),
});
export type ExpenseCategoryItem = z.infer<typeof ExpenseCategoryItem>;

export const ExpenseCategoryList = z.object({
  /** Mois restants de l'année en cours (ceux qu'un éclatement peut transférer). */
  months: z.array(MonthKey),
  categories: z.array(ExpenseCategoryItem),
});
export type ExpenseCategoryList = z.infer<typeof ExpenseCategoryList>;

// ------------------------------------------------------------------ Estimation de fin d'année

export const EstimateComputedSource = z.enum(['ai', 'average', 'seasonal', 'import']);
export type EstimateComputedSource = z.infer<typeof EstimateComputedSource>;

export const EstimateCell = z.object({
  month: MonthKey,
  /** Valeur retenue : saisie à la main, sinon calculée, sinon prévision du Sheet, sinon moyenne. */
  valueCents: Cents,
  origin: z.enum(['manual', 'ai', 'average', 'seasonal', 'import']),
  /** Estimation calculée enregistrée (null si jamais calculée pour ce mois). */
  computedCents: Cents.nullable(),
  computedSource: EstimateComputedSource.nullable(),
  /** Valeur saisie à la main (null = pas de saisie, « réinitialiser » la retire). */
  manualCents: Cents.nullable(),
  /** Explication courte (Claude) ou mode de calcul. */
  note: z.string().nullable(),
  /** Mois en cours : déjà dépensé à date pour ce poste. */
  actualCents: Cents,
});
export type EstimateCell = z.infer<typeof EstimateCell>;

/** `GET /api/expenses/estimates` : mois restants de l'année en cours, par poste. */
export const ExpenseEstimates = z.object({
  year: z.int(),
  /** Du mois en cours à décembre. */
  months: z.array(MonthKey),
  /** Dernier calcul enregistré (null : jamais calculé, valeurs = prévisions du Sheet ou moyenne). */
  computedAt: IsoDateTime.nullable(),
  /** Mode du dernier calcul : Claude, ou sans Claude (moyenne / même mois de l'an dernier). */
  computedBy: z.enum(['ai', 'fallback']).nullable(),
  categories: z.array(
    z.object({
      categoryId: Uuid,
      name: z.string(),
      kind: z.enum(['fixed', 'variable']),
      cells: z.array(EstimateCell),
      totalCents: Cents,
    }),
  ),
  yearEnd: YearEndProjection,
});
export type ExpenseEstimates = z.infer<typeof ExpenseEstimates>;

/** `PUT /api/expenses/estimates/:categoryId/:month` : valeur saisie, `null` = réinitialiser au calcul. */
export const UpdateEstimateBody = z.object({ manualCents: Cents.min(0).max(100_000_000).nullable() });
export type UpdateEstimateBody = z.infer<typeof UpdateEstimateBody>;

/** `POST /api/admin/expenses/estimates/recompute` (admin). */
export const RecomputeEstimatesResult = z.object({
  computedBy: z.enum(['ai', 'fallback']),
  note: z.string(),
  estimates: ExpenseEstimates,
});
export type RecomputeEstimatesResult = z.infer<typeof RecomputeEstimatesResult>;

// ------------------------------------------------------------------ Lot Y (retours d'Inès du 13/10/2026)

// `settings.expenses` (seuil de « À vérifier ») est défini dans `settings.ts` (pas d'import circulaire).

/** Ligne d'un groupe (« À catégoriser », « À vérifier ») : de quoi l'afficher et ouvrir son détail. */
export const GroupLine = z.object({
  id: Uuid,
  date: IsoDate,
  label: z.string(),
  amountCents: Cents,
  isTaxIncluded: z.boolean(),
  counterparty: z.string().nullable(),
});
export type GroupLine = z.infer<typeof GroupLine>;

/** `GET /api/expenses/to-categorize` [13] : par compte Axonaut, puis par contrepartie, puis lignes isolées. */
export const UncategorizedGroups = z.object({
  /** Lignes à catégoriser en tout. */
  total: z.int().min(0),
  codeGroups: z.array(
    z.object({
      code: z.string(),
      /** Libellé du compte dans Axonaut, s'il figure dans les données reçues. */
      axonautLabel: z.string().nullable(),
      count: z.int().min(1),
      totalCents: Cents,
      /** Au moins une ligne bancaire (montant TTC). */
      isTaxIncluded: z.boolean(),
      lines: z.array(GroupLine),
    }),
  ),
  counterpartyGroups: z.array(
    z.object({
      /** Motif de la règle créée par « Créer une règle pour les prochaines lignes ». */
      pattern: z.string(),
      counterparty: z.string(),
      count: z.int().min(1),
      totalCents: Cents,
      isTaxIncluded: z.boolean(),
      lines: z.array(GroupLine),
    }),
  ),
  isolated: z.array(GroupLine),
});
export type UncategorizedGroups = z.infer<typeof UncategorizedGroups>;

/**
 * `POST /api/expenses/to-categorize/apply` : un choix range tout le groupe (poste « vous ») ; avec `remember`,
 * le compte rejoint les comptes du poste, ou une règle est créée sur la contrepartie (lignes futures comprises).
 * Lot AH [20] : à la place d'un poste, un motif « Pas une dépense » (`excludedReason`) s'applique aux lignes du
 * groupe comme dans le tableau (`excluded_by = 'user'`, réversible ligne par ligne) ; `remember` est alors sans
 * effet (il n'existe pas de règle « Pas une dépense » apprise).
 */
const ApplyGroupChoice = {
  categoryId: Uuid.optional(),
  excludedReason: ExpenseExclusionReason.optional(),
  remember: z.boolean(),
};
export const ApplyGroupBody = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('code'), code: z.string().trim().min(1).max(20), ...ApplyGroupChoice }),
    z.object({
      kind: z.literal('counterparty'),
      pattern: z.string().trim().min(1).max(200),
      ...ApplyGroupChoice,
    }),
  ])
  .refine((b) => (b.categoryId === undefined) !== (b.excludedReason === undefined), {
    message: 'Choisis un poste ou un motif « Pas une dépense ».',
  });
export type ApplyGroupBody = z.infer<typeof ApplyGroupBody>;

export const ApplyGroupResult = z.object({
  /** Lignes rangées (celles du groupe, et pour un compte ou une règle, les autres lignes concernées). */
  categorized: z.int().min(0),
  /** Compte ajouté aux comptes du poste (`remember` d'un compte). */
  rememberedCode: z.string().nullable(),
  /** Règle créée (`remember` d'une contrepartie). */
  ruleId: Uuid.nullable(),
  /** Lot AH [20] : lignes du groupe marquées « Pas une dépense ». */
  excluded: z.int().min(0),
});
export type ApplyGroupResult = z.infer<typeof ApplyGroupResult>;

/** Origine d'une correspondance compte → poste [15] : par défaut, ajoutée par un membre, ou inconnue. */
export const AccountCodeOrigin = z.object({
  kind: z.enum(['default', 'user', 'unknown']),
  byName: z.string().nullable(),
  at: IsoDateTime.nullable(),
});
export type AccountCodeOrigin = z.infer<typeof AccountCodeOrigin>;

/** `GET /api/expenses/account-codes` [15] : comptes Axonaut → postes du Cockpit. */
export const AccountCodes = z.object({
  year: z.int(),
  rows: z.array(
    z.object({
      code: z.string(),
      axonautLabel: z.string().nullable(),
      categoryId: Uuid,
      categoryName: z.string(),
      origin: AccountCodeOrigin,
      /** Dépenses de l'année rangées par ce compte (le préfixe le plus précis l'emporte), montant compté. */
      yearCents: Cents,
      lineCount: z.int().min(0),
    }),
  ),
});
export type AccountCodes = z.infer<typeof AccountCodes>;

/** `PUT /api/expenses/account-codes` : ajoute un compte à un poste (ou le déplace). */
export const SetAccountCodeBody = z.object({
  code: z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[0-9A-Za-z ]+$/, 'Un numéro de compte (chiffres) est attendu'),
  categoryId: Uuid,
});
export type SetAccountCodeBody = z.infer<typeof SetAccountCodeBody>;

/** `GET /api/expenses/review` [16] : prédictions peu sûres au-dessus du seuil, par contrepartie et poste. */
export const ReviewGroups = z.object({
  minCents: Cents,
  /** Choix du seuil (0 = toutes). */
  choices: z.array(Cents),
  /** Prédictions peu sûres en tout (quel que soit le montant). */
  totalReview: z.int().min(0),
  /** Lignes au-dessus du seuil. */
  shownCount: z.int().min(0),
  groups: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      categoryId: Uuid,
      categoryName: z.string(),
      count: z.int().min(1),
      totalCents: Cents,
      isTaxIncluded: z.boolean(),
      confidence: z.number(),
      lines: z.array(GroupLine),
    }),
  ),
});
export type ReviewGroups = z.infer<typeof ReviewGroups>;

/** `PUT /api/expenses/review/threshold` (membres). */
export const SetReviewThresholdBody = z.object({ reviewMinCents: Cents.min(0).max(100_000_000) });
export type SetReviewThresholdBody = z.infer<typeof SetReviewThresholdBody>;

/** `POST /api/expenses/review/validate` : le poste proposé devient un choix humain (`user`, exemple pour l'IA). */
export const ValidateReviewBody = z.object({ expenseIds: z.array(Uuid).min(1).max(5000) });
export type ValidateReviewBody = z.infer<typeof ValidateReviewBody>;
export const ValidateReviewResult = z.object({ validated: z.int().min(0) });
export type ValidateReviewResult = z.infer<typeof ValidateReviewResult>;

/**
 * Catégorisation par l'IA de tout ce qui reste [14] (`POST/GET /api/expenses/categorize-ai`) : file pg-boss, lots
 * de 50, progression enregistrée. `queued`/`running` : en cours ; `done` ; `cap_reached` : arrêt au plafond
 * mensuel ; `error` ; `not_configured` : Claude non configuré ; `nothing` : rien à ranger.
 */
export const ExpenseAiRunStatus = z.enum([
  'queued',
  'running',
  'done',
  'cap_reached',
  'error',
  'not_configured',
  'nothing',
]);
export type ExpenseAiRunStatus = z.infer<typeof ExpenseAiRunStatus>;

export const ExpenseAiRun = z.object({
  id: Uuid,
  status: ExpenseAiRunStatus,
  /** Démonstration : prédictions par mots-clés, sans Claude. */
  demo: z.boolean(),
  startedAt: IsoDateTime,
  finishedAt: IsoDateTime.nullable(),
  startedByName: z.string().nullable(),
  /** Lignes à traiter au lancement. */
  total: z.int().min(0),
  /** Paquets de 50 : nombre total et paquets terminés. */
  batchCount: z.int().min(0),
  batchesDone: z.int().min(0),
  /** Lignes soumises, rangées, et parmi elles peu sûres (« À vérifier »). */
  attempted: z.int().min(0),
  categorized: z.int().min(0),
  review: z.int().min(0),
  note: z.string(),
});
export type ExpenseAiRun = z.infer<typeof ExpenseAiRun>;

export const ExpenseAiRunResponse = z.object({
  run: ExpenseAiRun.nullable(),
  /** `POST` alors qu'un passage est déjà en cours : rien n'est relancé. */
  alreadyRunning: z.boolean(),
});
export type ExpenseAiRunResponse = z.infer<typeof ExpenseAiRunResponse>;

/** `GET /api/expenses/:id/detail` [17] : tout ce qu'on sait d'une ligne (pas de lien Axonaut : il n'existe pas). */
export const ExpenseLineDetail = z.object({
  expense: Expense,
  /** Libellé bancaire complet (ligne bancaire). */
  bankLabel: z.string().nullable(),
  /** Titre et numéro de la dépense Axonaut. */
  title: z.string().nullable(),
  /** Commentaire saisi dans Axonaut. */
  comments: z.string().nullable(),
  /** Compte bancaire (nom lu dans Axonaut). */
  bankAccount: z.string().nullable(),
  /** Justificatif dans Axonaut : présent, absent, ou inconnu (donnée non fournie par Axonaut). */
  attachment: z.enum(['present', 'absent', 'unknown']),
  /** Libellé du compte comptable dans Axonaut. */
  accountingLabel: z.string().nullable(),
  /** Raison donnée par l'IA (une phrase), si elle a proposé un poste. */
  aiReason: z.string().nullable(),
});
export type ExpenseLineDetail = z.infer<typeof ExpenseLineDetail>;
